<div
  [class.justify-end]="isMe()"
  [class.mt-1]="!isFirstInGroup() && !isSameGroupAsPrevious()"
  class="flex w-full gap-2 items-center group"
>
  <!-- Avatar for other participant in a group chat -->
  @if (!isMe() && showAvatar()) {
    @if (isLastInGroup()) {
      <div
        class="size-8 rounded-full bg-gradient-to-br from-primary-9 to-indigo-9 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 shadow-sm"
      >
        {{ participantInitial() }}
      </div>
    } @else {
      <div class="w-8 flex-shrink-0"></div>
    }
  }

  <!-- Message text & time bubble -->
  <div
    [class]="
      isMe()
        ? 'bg-neutral-12 text-white dark:text-neutral-2'
        : 'bg-white dark:bg-neutral-3/40 text-neutral-12'
    "
    [class.rounded-tr-[4px]]="isMe() && isSameGroupAsPrevious()"
    [class.rounded-br-[4px]]="isMe() && !isLastInGroup()"
    [class.rounded-tl-[4px]]="!isMe() && isSameGroupAsPrevious()"
    [class.rounded-bl-[4px]]="!isMe() && !isLastInGroup()"
    [class.ring-2]="isReplyingTo()"
    [class.ring-primary-9]="isReplyingTo() && !isMe()"
    [class.ring-white]="isReplyingTo() && isMe()"
    [class.ring-offset-2]="isReplyingTo()"
    [class.dark:ring-offset-neutral-2]="isReplyingTo()"
    class="chat-bubble-content max-w-[70%] rounded-[18px] px-3.5 py-2 text-sm leading-relaxed border border-neutral-3 dark:border-neutral-3/25 shadow-sm whitespace-pre-wrap flow-root transition-all duration-200"
  >
    @if (replyToText()) {
      <div
        role="button"
        tabindex="0"
        [class]="
          isMe()
            ? 'bg-white/10 hover:bg-white/25 border-white focus-visible:ring-white dark:bg-neutral-2/10 dark:hover:bg-neutral-2/20 dark:border-neutral-3'
            : 'bg-neutral-2/50 hover:bg-neutral-3/50 dark:bg-neutral-3/50 dark:hover:bg-neutral-3/70 border-primary-9 focus-visible:ring-primary-9'
        "
        (click)="replyToClick.emit()"
        (keydown.enter)="replyToClick.emit()"
        (keydown.space)="$event.preventDefault(); replyToClick.emit()"
        class="border-l-[3px] rounded-r p-2 mb-1.5 text-xs select-none flex flex-col gap-0.5 max-w-full overflow-hidden cursor-pointer transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-neutral-9"
      >
        <span
          [class]="isMe() ? 'text-white/90 dark:text-neutral-2/90' : 'text-primary-11'"
          class="font-bold text-[10px] leading-tight uppercase tracking-wider"
        >
          {{ replyToSenderName() || 'Message' }}
        </span>
        <span
          [class]="isMe() ? 'text-white/80 dark:text-neutral-2/80' : 'text-neutral-11'"
          class="truncate block max-w-full leading-normal"
        >
          {{ replyToText() }}
        </span>
      </div>
    }
    <span>{{ text() }}</span>
    @if (isLastInGroup() || failed()) {
      <span
        [class.text-white/60]="isMe()"
        [class.dark:text-neutral-2/60]="isMe()"
        [class.text-neutral-10]="!isMe()"
        class="inline-flex items-center gap-1 text-[9px] font-semibold ml-2 float-right mt-0.5 select-none"
      >
        {{ time() }}
        @if (isMe()) {
          @if (sending()) {
            <mat-icon
              class="!w-3 !h-3 !text-[12px] shrink-0 opacity-80 flex items-center justify-center animate-spin"
              >sync</mat-icon
            >
          } @else if (isReadByAll()) {
            <mat-icon
              class="!w-3 !h-3 !text-[12px] shrink-0 text-blue-9 dark:text-blue-9 flex items-center justify-center font-bold"
              >done_all</mat-icon
            >
          } @else if (failed()) {
            <mat-icon
              [matTooltip]="errorMessage() || 'Échec de l\'envoi'"
              class="!w-3 !h-3 !text-[12px] shrink-0 text-red-9 dark:text-red-9 flex items-center justify-center cursor-help"
              >error</mat-icon
            >
          } @else {
            <mat-icon class="!w-3 !h-3 !text-[12px] shrink-0 opacity-80 flex items-center justify-center"
              >done</mat-icon
            >
          }
        }
      </span>
    }
  </div>

  <!-- Retry button (always visible when failed) -->
  @if (failed()) {
    <button
      type="button"
      (click)="retry.emit()"
      title="Renvoyer le message"
      [class.order-first]="isMe()"
      class="size-7 rounded-full bg-red-1 dark:bg-red-9/10 border border-red-3 text-red-9 hover:text-red-11 flex items-center justify-center hover:bg-red-2 hover:scale-105 active:scale-95 transition-all shadow-sm cursor-pointer shrink-0"
    >
      <mat-icon class="!w-4 !h-4 !text-[16px] flex items-center justify-center">refresh</mat-icon>
    </button>
  } @else {
    <!-- Message Hover Action Bar -->
    <div
      [class.order-first]="isMe()"
      class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity focus-within:opacity-100 select-none shrink-0"
    >
      <!-- Reply Button -->
      <button
        type="button"
        (click)="reply.emit()"
        title="Répondre"
        class="size-7 rounded-full bg-white dark:bg-neutral-2 border border-neutral-3 text-neutral-11 hover:text-neutral-12 flex items-center justify-center hover:bg-neutral-2 hover:scale-105 active:scale-95 transition-all shadow-sm cursor-pointer"
      >
        <mat-icon class="!w-4 !h-4 !text-[16px] flex items-center justify-center">reply</mat-icon>
      </button>

      <!-- More Button -->
      <button
        type="button"
        [matMenuTriggerFor]="bubbleMenu"
        title="Plus d'actions"
        class="size-7 rounded-full bg-white dark:bg-neutral-2 border border-neutral-3 text-neutral-11 hover:text-neutral-12 flex items-center justify-center hover:bg-neutral-2 hover:scale-105 active:scale-95 transition-all shadow-sm cursor-pointer"
      >
        <mat-icon class="!w-4 !h-4 !text-[16px] flex items-center justify-center">more_horiz</mat-icon>
      </button>

      <!-- Mat Menu for Bubble actions -->
      <mat-menu
        #bubbleMenu="matMenu"
        xPosition="before"
        class="rounded-2xl border border-neutral-3 shadow-lg"
      >
        <button mat-menu-item (click)="copyText.emit()">
          <mat-icon class="text-neutral-11">content_copy</mat-icon>
          <span>Copier le texte</span>
        </button>
        <button mat-menu-item (click)="pin.emit()">
          <mat-icon class="text-neutral-11">push_pin</mat-icon>
          <span>Épingler</span>
        </button>
        <button mat-menu-item (click)="info.emit()">
          <mat-icon class="text-neutral-11">info</mat-icon>
          <span>Infos</span>
        </button>
        @if (showDelete()) {
          <button mat-menu-item (click)="delete.emit()" class="text-red-9 dark:text-red-9">
            <mat-icon class="text-red-9 dark:text-red-9">delete</mat-icon>
            <span>Supprimer</span>
          </button>
        }
      </mat-menu>
    </div>
  }
</div>
